<template>
  <div class="kb-page">
    <AppHeader />

    <!-- Page Hero -->
    <section class="kb-page-hero" :style="{ backgroundImage: 'url(' + bgImage + ')' }">
      <div class="kb-page-hero__overlay"></div>
      <div class="container-xl kb-page-hero__content" data-aos="fade-up">
        <div class="kb-page-hero__breadcrumb">
          <router-link to="/">Home</router-link>
          <i class="bi bi-chevron-right mx-2"></i>
          <span>About Us</span>
        </div>
        <h1>About KeBee Industries</h1>
        <p>More than manufacturers — the frontline of protection for what matters most</p>
      </div>
    </section>

    <!-- Profile Section -->
    <section class="kb-section">
      <div class="container-xl">
        <div class="kb-about-intro">
          <div class="kb-about-intro__content" data-aos="fade-right">
            <span class="kb-section-header__tag">Our Identity</span>
            <h2>The KeBee Blueprint</h2>
            <p>At KeBee Tarpaulins, we are more than just manufacturers; we are the frontline of protection for your most valuable assets. With a foundation built on industrial expertise and a passion for material science, KeBee has emerged as a premier provider of heavy-duty covering solutions.</p>
            <p>From the rugged fields of agriculture to the high-stakes environment of industrial logistics, our diverse product range — including HDPE, PVC-coated fabrics, and specialized agro-nets — is designed to withstand the toughest conditions on the planet. We combine traditional durability with modern engineering to deliver products that don't just cover; <strong>they secure.</strong></p>
            <div class="kb-about-stat-row">
              <div v-for="stat in stats" :key="stat.label" class="kb-about-stat">
                <span class="num">{{ stat.value }}</span>
                <span class="lbl">{{ stat.label }}</span>
              </div>
            </div>
          </div>
          <div class="kb-about-intro__images" data-aos="fade-left" data-aos-delay="100">
            <img src="@/assets/slider2.webp" alt="KeBee Tarpaulin Manufacturing" class="kb-about-intro__img-top" loading="lazy" />
            <img src="@/assets/fire-retardant-tarpaulin-500x500.webp" alt="KeBee Products" class="kb-about-intro__img-bottom" loading="lazy" />
          </div>
        </div>
      </div>
    </section>

    <!-- Vision & Mission -->
    <section class="kb-section kb-section--light">
      <div class="container-xl">
        <div class="kb-section-header" data-aos="fade-up">
          <span class="kb-section-header__tag">Our North Star</span>
          <h2>Vision & Mission</h2>
        </div>
        <div class="kb-vm-grid">
          <div class="kb-vm-card kb-vm-card--vision" data-aos="fade-up" data-aos-delay="0">
            <div class="kb-vm-card__icon"><i class="bi bi-eye-fill"></i></div>
            <h3>Our Vision</h3>
            <h4>Shielding the Future</h4>
            <p>Our goal is to redefine the standard of protective textiles globally. At KeBee, we envision a future where every construction site, farm, and transport fleet is shielded by sustainable, high-performance materials.</p>
            <p>We strive to be the most trusted name in the industry by continuously innovating our polymer blends and weaving techniques — not just providing a product for today, but building a <strong>legacy of resilience</strong> that empowers our customers to operate with confidence, regardless of the weather.</p>
          </div>
          <div class="kb-vm-card kb-vm-card--mission" data-aos="fade-up" data-aos-delay="100">
            <div class="kb-vm-card__icon"><i class="bi bi-bullseye"></i></div>
            <h3>Our Mission</h3>
            <h4>The KeBee Gold Standard</h4>
            <p>Quality isn't a department at KeBee — it's our DNA. Our commitment starts at the molecular level, using 100% Virgin Quality polymers that ensure superior tensile strength and longevity.</p>
            <p>Every square inch of a KeBee product undergoes rigorous testing to ensure it delivers on its promise — from the smallest agro net to the heaviest industrial PVC tarpaulin. We don't accept "good enough."</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Quality Section -->
    <section class="kb-section">
      <div class="container-xl">
        <div class="kb-quality">
          <div class="kb-quality__content" data-aos="fade-right">
            <span class="kb-section-header__tag">Quality</span>
            <h2>The KeBee Gold Standard:<br>Beyond the Surface</h2>
            <p>Quality isn't a department at KeBee — it's our DNA. Our commitment starts at the molecular level, using <strong>100% Virgin Quality polymers</strong> that ensure superior tensile strength and longevity. Every square inch of a KeBee Tarpaulin undergoes rigorous testing:</p>
            <div class="kb-quality__tests">
              <div v-for="test in qualityTests" :key="test.title" class="kb-quality__test">
                <div class="kb-quality__test-icon"><i :class="test.icon"></i></div>
                <div>
                  <h4>{{ test.title }}</h4>
                  <p>{{ test.desc }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="kb-quality__visual" data-aos="fade-left" data-aos-delay="100">
            <div class="kb-quality__circles">
              <div class="kb-quality__circle kb-quality__circle--1">
                <span class="num">100%</span>
                <span class="lbl">Virgin<br>Polymers</span>
              </div>
              <div class="kb-quality__circle kb-quality__circle--2">
                <span class="num">750+</span>
                <span class="lbl">GSM<br>Max Weight</span>
              </div>
              <div class="kb-quality__circle kb-quality__circle--3">
                <span class="num">1m</span>
                <span class="lbl">Eyelet<br>Spacing</span>
              </div>
            </div>
            <img src="@/assets/20220113150406855.webp" alt="KeBee Quality Tarpaulin" class="kb-quality__img" loading="lazy" />
          </div>
        </div>
      </div>
    </section>

    <!-- Why KeBee -->
    <section class="kb-section kb-section--light">
      <div class="container-xl">
        <div class="kb-section-header" data-aos="fade-up">
          <span class="kb-section-header__tag">Why Partner With Us</span>
          <h2>The KeBee Edge</h2>
          <p>In a market flooded with recycled, low-grade alternatives, here is why industry leaders choose us</p>
        </div>
        <div class="kb-edge-grid">
          <div v-for="(item, i) in edgeItems" :key="item.title" class="kb-edge-card" data-aos="fade-up" :data-aos-delay="i * 80">
            <div class="kb-edge-card__header">
              <div class="kb-edge-card__icon"><i :class="item.icon"></i></div>
              <div class="kb-edge-card__num">0{{ i + 1 }}</div>
            </div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Team / CTA -->
    <section class="kb-about-cta">
      <div class="container-xl">
        <div class="kb-about-cta__inner" data-aos="fade-up">
          <h2>Ready to Partner with India's Trusted Manufacturer?</h2>
          <p>Get custom-sized, bulk-order tarpaulins and protection solutions delivered pan-India. Contact our team today for pricing and specifications.</p>
          <div class="kb-about-cta__actions">
            <a href="https://wa.me/919892310123?text=Hello%2C%20I%20would%20like%20to%20enquire%20about%20your%20products" target="_blank" class="kb-btn kb-btn--primary">
              <i class="bi bi-whatsapp me-2"></i>WhatsApp Us
            </a>
            <a href="mailto:info@kebeeindustries.com" class="kb-btn kb-btn--outline-white">
              <i class="bi bi-envelope me-2"></i>Send Email
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Map & Address -->
    <section class="kb-section">
      <div class="container-xl">
        <div class="kb-section-header" data-aos="fade-up">
          <span class="kb-section-header__tag">Find Us</span>
          <h2>Visit Our Shop</h2>
        </div>
        <div class="kb-location">
          <div class="kb-location__map" data-aos="fade-up">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3767.7932341105497!2d72.84082697493284!3d19.20423124793489!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b7e24d49d347%3A0x901e8eca21841dae!2sKeBee%20Tarpaulins%20and%20Plastic%20Industries!5e0!3m2!1sen!2sin!4v1745786880737!5m2!1sen!2sin"
              frameborder="0"
              style="border:0; width: 100%; height: 400px; border-radius: 16px;"
              allowfullscreen=""
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
          <div class="kb-location__info" data-aos="fade-up" data-aos-delay="100">
            <div class="kb-location__item">
              <i class="bi bi-geo-alt-fill"></i>
              <div>
                <h4>Address</h4>
                <p>Kala Darshan, 5, MG Crossword No. 3, opp. Achija Hotel, nr. Saraswat Bank, Kandivali West, Mumbai, Maharashtra 400067</p>
              </div>
            </div>
            <div class="kb-location__item">
              <i class="bi bi-telephone-fill"></i>
              <div>
                <h4>Phone</h4>
                <a href="Tel:+919892310123">+91 98923 10123</a>
              </div>
            </div>
            <div class="kb-location__item">
              <i class="bi bi-envelope-fill"></i>
              <div>
                <h4>Email</h4>
                <a href="mailto:info@kebeeindustries.com">info@kebeeindustries.com</a>
              </div>
            </div>
            <div class="kb-location__item">
              <i class="bi bi-clock-fill"></i>
              <div>
                <h4>Business Hours</h4>
                <p>Monday – Saturday: 9:00 AM – 6:00 PM</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import { setSeo } from '@/utils/seo.js'
import bgImage from '@/assets/about-bg.webp'

export default {
  name: 'AboutPage',
  components: { AppHeader, AppFooter },
  mounted() {
    setSeo({
      title: 'About KeBee Industries – Tarpaulin Manufacturer in Kandivali West, Mumbai',
      description: 'Learn about KeBee Industries – a Mumbai-based manufacturer of premium HDPE, PVC-FR & Canvas Tarpaulins, Agro Shade Nets and Nylon Ropes. 10+ years of excellence, 100% Virgin polymer quality, ISO-tested products.',
      path: '/about',
      keywords: 'KeBee Industries about, tarpaulin manufacturer Mumbai, Kandivali West, virgin polymer tarpaulin'
    })
  },
  data() {
    return {
      bgImage,
      stats: [
        { value: '100%', label: 'Proven Quality' },
        { value: '5K+', label: 'Happy Customers' },
        { value: '6', label: 'Product Lines' },
        // { value: '100%', label: 'Virgin Quality' },
      ],
      qualityTests: [
        { icon: 'bi bi-droplet-fill', title: 'Hydrostatic Pressure', desc: 'Ensuring 100% waterproof performance under real-world water pressure conditions.' },
        { icon: 'bi bi-sun-fill', title: 'UV Resistance', desc: 'Accelerated UV exposure testing for long-term sun performance without degradation.' },
        { icon: 'bi bi-lightning-charge-fill', title: 'Tear & Tensile Strength', desc: 'Guaranteeing our 750–1000 gauge plastics and high-GSM fabrics handle extreme tension.' },
        { icon: 'bi bi-gear-fill', title: 'Precision Engineering', desc: 'Rust-proof aluminum eyelets, heat-sealed leak-proof seams — no detail is left to chance.' },
      ],
      edgeItems: [
        { icon: 'bi bi-gem', title: 'Uncompromising Raw Materials', desc: 'We exclusively use 100% Virgin HDPE and PVC, providing better flexibility and a longer lifespan than the competition.' },
        { icon: 'bi bi-sliders', title: 'Tailored Versatility', desc: 'With a massive range of weights (125 to 750+ GSM) and thicknesses (up to 1000 Gauge), we have the exact solution for your specific load.' },
        { icon: 'bi bi-link-45deg', title: 'Reinforced by Design', desc: '"Every 1 Meter" eyelet placement and double-corner reinforcement ensure your cover stays put, even in high-wind zones.' },
        { icon: 'bi bi-bag-check-fill', title: 'Complete Protection Ecosystem', desc: 'From tarps to KeBee Nylon Ropes — we provide a one-stop-shop for total security.' },
        { icon: 'bi bi-currency-rupee', title: 'Direct-to-Customer Value', desc: 'Premium-grade specifications at competitive prices — the best "protection-per-square-foot" in the industry.' },
      ],
    }
  },
}
</script>

<style scoped>
/* ── Page Hero ── */
.kb-page-hero {
  min-height: 400px;
  background-size: cover;
  background-position: center;
  position: relative;
  display: flex;
  align-items: flex-end;
  padding-bottom: 60px;
}
.kb-page-hero__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(15,31,61,0.7), rgba(15,31,61,0.85));
}
.kb-page-hero__content {
  position: relative;
  z-index: 2;
  padding-top: 120px;
}
.kb-page-hero__breadcrumb {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: rgba(255,255,255,0.65);
  margin-bottom: 1rem;
}
.kb-page-hero__breadcrumb a {
  color: rgba(255,255,255,0.65);
  text-decoration: none;
}
.kb-page-hero__breadcrumb a:hover { color: #F59E0B; }
.kb-page-hero__content h1 {
  font-family: 'Poppins', sans-serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  color: #fff;
  margin-bottom: 0.75rem;
}
.kb-page-hero__content p {
  color: rgba(255,255,255,0.75);
  font-size: 1.1rem;
}

/* ── About Intro ── */
.kb-about-intro {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;
}
.kb-about-intro__content h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  color: #0F1F3D;
  margin-bottom: 1.25rem;
}
.kb-about-intro__content p {
  color: #4B5563;
  line-height: 1.8;
  margin-bottom: 1rem;
}
.kb-about-stat-row {
  display: flex;
  gap: 2rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #E5E7EB;
}
.kb-about-stat {
  display: flex;
  flex-direction: column;
}
.kb-about-stat .num {
  font-family: 'Poppins', sans-serif;
  font-size: 2rem;
  font-weight: 800;
  color: #F59E0B;
  line-height: 1;
}
.kb-about-stat .lbl {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9CA3AF;
  margin-top: 4px;
}
.kb-about-intro__images {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}
.kb-about-intro__img-top,
.kb-about-intro__img-bottom {
  width: 100%;
  border-radius: 16px;
  object-fit: cover;
  height: 220px;
}

/* ── Vision/Mission ── */
.kb-vm-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 2.5rem;
}
.kb-vm-card {
  padding: 2.5rem;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
}
.kb-vm-card--vision {
  background: linear-gradient(135deg, #0F1F3D 0%, #1E3A6E 100%);
  color: rgba(255,255,255,0.85);
}
.kb-vm-card--mission {
  background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
  color: rgba(255,255,255,0.9);
}
.kb-vm-card__icon {
  font-size: 2rem;
  margin-bottom: 1.25rem;
  color: rgba(255,255,255,0.6);
}
.kb-vm-card h3 {
  font-family: 'Poppins', sans-serif;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255,255,255,0.6);
  margin-bottom: 0.5rem;
}
.kb-vm-card h4 {
  font-family: 'Poppins', sans-serif;
  font-size: 1.4rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 1rem;
}
.kb-vm-card p {
  font-size: 0.9rem;
  line-height: 1.8;
  margin-bottom: 1rem;
}

/* ── Quality ── */
.kb-quality {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;
}
.kb-quality__content h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 1.9rem;
  font-weight: 700;
  color: #0F1F3D;
  margin-bottom: 1.25rem;
}
.kb-quality__content > p {
  color: #4B5563;
  line-height: 1.8;
  margin-bottom: 1.75rem;
}
.kb-quality__tests {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.kb-quality__test {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}
.kb-quality__test-icon {
  width: 40px;
  height: 40px;
  background: rgba(245,158,11,0.1);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
  color: #F59E0B;
  flex-shrink: 0;
}
.kb-quality__test h4 {
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0F1F3D;
  margin: 0 0 4px;
}
.kb-quality__test p {
  font-size: 0.84rem;
  color: #6B7280;
  margin: 0;
  line-height: 1.6;
}
.kb-quality__visual {
  position: relative;
}
.kb-quality__img {
  width: 100%;
  border-radius: 20px;
  object-fit: cover;
  height: 400px;
}
.kb-quality__circles {
  position: absolute;
  z-index: 2;
  top: -20px;
  right: -20px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.kb-quality__circle {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}
.kb-quality__circle .num {
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  font-size: 1rem;
  line-height: 1;
  color: #fff;
}
.kb-quality__circle .lbl {
  font-size: 0.6rem;
  color: rgba(255,255,255,0.85);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  line-height: 1.3;
  margin-top: 2px;
}
.kb-quality__circle--1 { background: #0F1F3D; }
.kb-quality__circle--2 { background: #F59E0B; }
.kb-quality__circle--3 { background: #10B981; }

/* ── Edge Grid ── */
.kb-edge-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 2.5rem;
}
.kb-edge-card {
  background: #fff;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 16px rgba(15,31,61,0.06);
  transition: transform 0.3s, box-shadow 0.3s;
}
.kb-edge-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 28px rgba(15,31,61,0.1);
}
.kb-edge-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}
.kb-edge-card__icon {
  font-size: 1.5rem;
  color: #F59E0B;
}
.kb-edge-card__num {
  font-family: 'Poppins', sans-serif;
  font-size: 2rem;
  font-weight: 800;
  color: rgba(15,31,61,0.06);
}
.kb-edge-card h3 {
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #0F1F3D;
  margin-bottom: 0.75rem;
}
.kb-edge-card p {
  font-size: 0.875rem;
  color: #6B7280;
  line-height: 1.7;
  margin: 0;
}

/* ── About CTA ── */
.kb-about-cta {
  background: linear-gradient(135deg, #0F1F3D, #1E3A6E);
  padding: 80px 0;
  text-align: center;
}
.kb-about-cta__inner h2 {
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.5rem, 3vw, 2.25rem);
  font-weight: 700;
  color: #fff;
  margin-bottom: 1rem;
}
.kb-about-cta__inner p {
  color: rgba(255,255,255,0.72);
  font-size: 1.05rem;
  margin-bottom: 2rem;
}
.kb-about-cta__actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

/* ── Location ── */
.kb-location {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: start;
  margin-top: 2.5rem;
}
.kb-location__info {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.kb-location__item {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}
.kb-location__item i {
  color: #F59E0B;
  font-size: 1.2rem;
  margin-top: 3px;
  flex-shrink: 0;
}
.kb-location__item h4 {
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 4px;
}
.kb-location__item p, .kb-location__item a {
  color: #374151;
  font-size: 0.9rem;
  line-height: 1.6;
  text-decoration: none;
  margin: 0;
}
.kb-location__item a:hover { color: #F59E0B; }

@media (max-width: 992px) {
  .kb-about-intro, .kb-quality, .kb-location { grid-template-columns: 1fr; gap: 2rem; }
  .kb-vm-grid, .kb-edge-grid { grid-template-columns: 1fr 1fr; }
  .kb-about-stat-row { gap: 1.5rem; }
}
@media (max-width: 576px) {
  .kb-vm-grid, .kb-edge-grid { grid-template-columns: 1fr; }
  .kb-about-stat-row { flex-wrap: wrap; gap: 1.2rem; }
  .kb-about-stat { min-width: calc(50% - 0.6rem); }
  .kb-about-stat .num { font-size: 1.6rem; }
}
</style>
